import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Eye, EyeOff, ArrowRight, ArrowLeft, Camera, FileText, ShieldCheck, Check } from "lucide-react";
import { DIAL_CODES, PARTICIPATING_COUNTRIES } from "@/lib/countries";
import { motion, AnimatePresence } from "framer-motion";

// Time-based restrictions have been replaced by the global registration toggle

const NATIONALITIES = [...PARTICIPATING_COUNTRIES];

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Block the page entirely once registration closes
  useEffect(() => {
    api.get("/meta/events").then(({ data }) => {
      if (!data.registration_open) {
        toast.error("Account registration is now closed.");
        navigate("/login", { replace: true });
      }
    }).catch(() => {});
  }, [navigate]);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", gender: "Male",
    date_of_birth: "", nationality: "India", club_team: "",
    phone: "+91 ", identification_number: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    profile_photo: "",
    passport_document: "",
    passport_expiry_date: "",
    medical_certificate: "",
    participant_type: "Athlete"
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // Auto-update phone prefix when country changes
  const updateNationality = (val) => {
    setForm((p) => {
      const newDial = DIAL_CODES[val] || "+";
      const stripped = p.phone.replace(/^\+\d{1,4}\s*/, "");
      return { ...p, nationality: val, phone: `${newDial} ${stripped}`.trim() + (stripped ? "" : " ") };
    });
  };

  // Local file objects — held in memory, uploaded only on submit
  const [pendingFiles, setPendingFiles] = useState({ photo: null, passport: null, medical: null });

  const uploadToS3 = async (file, endpoint) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/${endpoint}`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Upload failed");
    }
    return (await res.json()).url;
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Photo must be under 3MB"); return; }
    setPendingFiles((p) => ({ ...p, photo: file }));
    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = () => update("profile_photo", reader.result);
    reader.readAsDataURL(file);
  };

  const onDoc = (field, pendingKey, maxMB = 5) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) { toast.error(`File must be under ${maxMB}MB`); return; }
    setPendingFiles((p) => ({ ...p, [pendingKey]: file }));
    update(field, file.name); // just mark as selected, URL set on submit
  };

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);

  // Strips a leading "+<dial code>" prefix (if any) and returns just the
  // digits of the subscriber number, so we can enforce exactly 10 digits
  // after the country code regardless of how it's formatted/spaced.
  const digitsAfterDialCode = (raw) => {
    const withoutDialCode = raw.replace(/^\+\d{1,4}\s*/, "");
    return withoutDialCode.replace(/\D/g, "");
  };

  const nextStep = () => {
    const isAdmin = user?.role === "admin";
    if (step === 1 && !isAdmin) {
      if (!form.full_name.trim()) return toast.error("Full name is required");
      if (!form.email.trim() || !form.email.includes("@")) return toast.error("A valid email is required");
      if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
      if (!form.profile_photo) return toast.error("Profile photo is required — face must be clearly visible");
    }
    if (step === 2 && !isAdmin) {
      if (!form.date_of_birth) return toast.error("Date of birth is required");
      const phoneDigits = digitsAfterDialCode(form.phone);
      if (!phoneDigits) return toast.error("Phone number is required");
      if (phoneDigits.length !== 10) return toast.error("Phone number must be exactly 10 digits after the country code");
      if (!form.club_team.trim()) return toast.error("Club / Team name is required");
      if (!form.identification_number.trim()) return toast.error(form.nationality === "India" ? "SFI UID is required" : "Passport Number is required");
      if (!form.emergency_contact_name.trim()) return toast.error("Emergency contact name is required");
      const emergencyDigits = digitsAfterDialCode(form.emergency_contact_phone);
      if (!emergencyDigits) return toast.error("Emergency contact phone number is required");
      if (emergencyDigits.length !== 10) return toast.error("Emergency contact phone number must be exactly 10 digits after the country code");
    }
    setDirection(1);
    setStep((s) => s + 1);
  };

  const prevStep = () => {
    setDirection(-1);
    setStep((s) => s - 1);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!pendingFiles.photo && !form.profile_photo) {
      toast.error("Profile photo is required — face must be clearly visible");
      return;
    }
    // Re-checked here (not just in nextStep) so it's enforced no matter how
    // step 3 was reached — including the admin preview path, which skips
    // the per-step validations above.
    if (digitsAfterDialCode(form.phone).length !== 10) {
      toast.error("Phone number must be exactly 10 digits after the country code");
      return;
    }
    if (digitsAfterDialCode(form.emergency_contact_phone).length !== 10) {
      toast.error("Emergency contact phone number must be exactly 10 digits after the country code");
      return;
    }
    if (!form.identification_number.trim()) {
      toast.error(form.nationality === "India" ? "SFI UID is required" : "Passport Number is required");
      return;
    }
    if (!pendingFiles.passport && !form.passport_document) {
      toast.error(form.nationality === "India" ? "SFI UID document is required" : "Passport copy is required");
      return;
    }
    if (!pendingFiles.medical && !form.medical_certificate) {
      toast.error("Medical fitness certificate is required");
      return;
    }
    if (!form.passport_expiry_date || !/^\d{2}-\d{2}$/.test(form.passport_expiry_date)) {
      toast.error(`${form.nationality === "India" ? "SFI UID" : "Passport"} expiry must be in MM-YY format`);
      return;
    }
    const [mm, yy] = form.passport_expiry_date.split("-");
    const month = parseInt(mm, 10);
    const year = parseInt(yy, 10) + 2000; // assuming 2000s

    if (month < 1 || month > 12) {
      toast.error(`Invalid month in ${form.nationality === "India" ? "SFI UID" : "passport"} expiry`);
      return;
    }

    if (year < 2027 || (year === 2027 && month < 3)) {
      toast.error(`${form.nationality === "India" ? "SFI UID" : "Passport"} is valid only if the expiry date is equal to March 2027 or higher`);
      return;
    }
    setLoading(true);
    try {
      // Uploaded one at a time (not Promise.all) — on a weak/mobile connection,
      // any file that succeeds is saved into `form` and cleared from
      // `pendingFiles` immediately, so a retry after a partial failure only
      // re-uploads whatever actually failed, instead of all three from scratch.
      toast.loading("Uploading files…", { id: "upload" });
      const uploads = [
        { key: "photo", field: "profile_photo", endpoint: "upload/photo" },
        { key: "passport", field: "passport_document", endpoint: "upload/document" },
        { key: "medical", field: "medical_certificate", endpoint: "upload/document" },
      ];
      const urls = {};
      for (const { key, field, endpoint } of uploads) {
        if (pendingFiles[key]) {
          const url = await uploadToS3(pendingFiles[key], endpoint);
          urls[field] = url;
          update(field, url);
          setPendingFiles((p) => ({ ...p, [key]: null }));
        } else {
          urls[field] = form[field];
        }
      }
      toast.dismiss("upload");

      const payload = {
        ...form,
        ...urls,
        passport_expiry_date: form.passport_expiry_date,
        emergency_contact: `${form.emergency_contact_name.trim()} (${form.emergency_contact_phone.trim()})`
      };
      delete payload.emergency_contact_name;
      delete payload.emergency_contact_phone;

      const user = await register(payload);
      toast.success(`Welcome ${user.full_name}! Your Masters category: ${user.masters_category}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.dismiss("upload");
      const errorDetail = err.response?.data?.detail;
      const message = errorDetail
        ? formatError(errorDetail)
        : err instanceof TypeError
          ? "Upload failed — check your connection and try again."
          : (err.message || "Something went wrong");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const stepsConfig = [
    { number: 1, label: "Account" },
    { number: 2, label: "Details" },
    { number: 3, label: "Verification" }
  ];

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.25, ease: "easeOut" }
    },
    exit: (dir) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" }
    })
  };

  return (
    <div className="min-h-dvh bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 px-4 sm:px-6 max-w-3xl mx-auto">
        <div className="eyebrow mb-3">AMSC India 2026 · Athlete Registration</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mb-2">
          Create your <span className="text-[#00F0FF]">athlete profile</span>
        </h1>
        <p className="text-[#94A3B8] mb-8">
          Once registered, your details are saved permanently — no need to re-enter them for future events.
        </p>

        <form
          onSubmit={onSubmit}
          onKeyDown={(e) => {
            // This whole multi-step flow lives in one <form>, so once step 3's
            // submit button exists in the DOM, pressing Enter in any field on
            // any step natively submits early — before documents are attached.
            // Only an explicit click on the actual submit button should submit;
            // Enter/Space while the button itself is focused still works.
            if (e.key === "Enter" && e.target.tagName !== "BUTTON") {
              e.preventDefault();
            }
          }}
          className="card-dark p-6 md:p-8 space-y-8 overflow-hidden"
          data-testid="register-form"
        >
          {/* Progress Indicator */}
          <div className="flex items-center justify-between mb-2 relative max-w-md mx-auto">
            {/* Background line */}
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-[#2A3A5F] -translate-y-1/2 z-0" />

            {/* Progress line fill */}
            <div
              className="absolute top-1/2 left-0 h-0.5 bg-[#00F0FF] -translate-y-1/2 z-0 transition-all duration-300"
              style={{ width: `${((step - 1) / 2) * 100}%` }}
            />

            {stepsConfig.map((sConfig) => {
              const isCompleted = step > sConfig.number;
              const isActive = step === sConfig.number;
              return (
                <div key={sConfig.number} className="relative z-10 flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-bold text-sm transition-all duration-300 ${isCompleted
                        ? "bg-[#00E676] border-[#00E676] text-black"
                        : isActive
                          ? "bg-[#0B1429] border-[#00F0FF] text-[#00F0FF] shadow-[0_0_15px_rgba(0,240,255,0.4)]"
                          : "bg-[#142040] border-[#2A3A5F] text-[#64748B]"
                      }`}
                  >
                    {isCompleted ? <Check className="w-5 h-5 stroke-[3px]" /> : sConfig.number}
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold mt-2 transition-colors duration-300 ${isActive ? "text-[#00F0FF]" : "text-[#64748B]"}`}>
                    {sConfig.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="divider-line my-6" />

          {/* Steps Container */}
          <div className="relative min-h-[300px]">
            <AnimatePresence mode="wait" custom={direction}>
              {step === 1 && (
                <motion.div
                  key="step-1"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="space-y-6"
                >
                  <div className="flex items-start gap-4 p-4 rounded-lg border border-[#FF6B6B]/30 bg-[#FF6B6B]/5">
                    <label
                      className={`w-20 h-20 rounded-full border-2 flex items-center justify-center cursor-pointer overflow-hidden shrink-0 transition ${form.profile_photo ? "border-[#00E676]" : "border-dashed border-[#FF6B6B]"
                        }`}
                      data-testid="photo-upload"
                      style={{ background: form.profile_photo ? "transparent" : "#142040" }}
                    >
                      {form.profile_photo ? (
                        <img src={form.profile_photo} alt="profile" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-7 h-7 text-[#FF6B6B]" />
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                    </label>
                    <div className="text-sm">
                      <div className="font-semibold flex items-center gap-2">
                        Profile Photo <span className="text-[#FF6B6B]">*</span>
                        {form.profile_photo && <Check className="w-4 h-4 text-[#00E676]" />}
                      </div>
                      <div className="text-xs text-[#FF6B6B] font-semibold mt-1">MANDATORY · FACE MUST BE CLEARLY VISIBLE</div>
                      <div className="text-xs text-[#94A3B8] mt-1">
                        Passport-style headshot. Front-facing. Good lighting. No sunglasses, hats, or filters. PNG/JPG · Max 3MB.
                      </div>
                    </div>
                  </div>

                  <Field label="Registering As" required>
                    <select data-testid="reg-participant-type" value={form.participant_type} onChange={(e) => update("participant_type", e.target.value)} className="input-dark">
                      <option>Athlete</option>
                      <option>Coach</option>
                      <option>Manager</option>
                    </select>
                  </Field>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Full Name" required><input data-testid="reg-name" required autoComplete="name" value={form.full_name} onChange={(e) => update("full_name", e.target.value.replace(/[^a-zA-Z\s\-\']/g, ""))} className="input-dark" /></Field>
                    <Field label="Email" required><input data-testid="reg-email" type="email" required autoComplete="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input-dark" /></Field>
                    <div className="md:col-span-2">
                      <Field label="Password (min 8 chars)" required>
                        <div className="relative">
                          <input data-testid="reg-password" type={showPwd ? "text" : "password"} required minLength={8} autoComplete="new-password" value={form.password} onChange={(e) => update("password", e.target.value)} className="input-dark pr-12" />
                          <button type="button" onClick={() => setShowPwd(!showPwd)} aria-label={showPwd ? "Hide password" : "Show password"} className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-[#94A3B8] hover:text-[#00F0FF] touch-manipulation" data-testid="toggle-password">
                            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </Field>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step-2"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="grid md:grid-cols-2 gap-4"
                >
                  <Field label="Date of Birth" required><input data-testid="reg-dob" type="date" required value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className="input-dark" /></Field>
                  <Field label="Gender" required>
                    <select data-testid="reg-gender" value={form.gender} onChange={(e) => update("gender", e.target.value)} className="input-dark">
                      <option>Male</option><option>Female</option><option>Other</option>
                    </select>
                  </Field>
                  <Field label="Nationality" required>
                    <select data-testid="reg-nationality" value={form.nationality} onChange={(e) => updateNationality(e.target.value)} className="input-dark">
                      {NATIONALITIES.map((n) => <option key={n}>{n}</option>)}
                    </select>
                  </Field>
                  <Field label="Phone Number" required><input data-testid="reg-phone" required autoComplete="tel" inputMode="tel" value={form.phone} onChange={(e) => update("phone", e.target.value.replace(/[^\d\+\-\s\(\)]/g, ""))} className="input-dark" placeholder="+91 9876543210" /></Field>
                  <Field label="Club / Team" required><input data-testid="reg-club" required value={form.club_team} onChange={(e) => update("club_team", e.target.value)} className="input-dark" /></Field>
                  <Field label={form.nationality === "India" ? "SFI UID" : "Passport Number"} required>
                    <input data-testid="reg-passport" required value={form.identification_number} onChange={(e) => update("identification_number", e.target.value)} className="input-dark" />
                  </Field>
                  <Field label="Emergency Contact Name" required>
                    <input
                      data-testid="reg-emergency-name"
                      required
                      value={form.emergency_contact_name}
                      onChange={(e) => update("emergency_contact_name", e.target.value.replace(/[^a-zA-Z\s\-\']/g, ""))}
                      className="input-dark"
                      placeholder="e.g. Jane Doe"
                    />
                  </Field>
                  <Field label="Emergency Contact Phone" required>
                    <input
                      data-testid="reg-emergency-phone"
                      required
                      value={form.emergency_contact_phone}
                      onChange={(e) => update("emergency_contact_phone", e.target.value.replace(/[^\d\+\-\s\(\)]/g, ""))}
                      className="input-dark"
                      placeholder="e.g. +91 98765 43210"
                    />
                  </Field>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step-3"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="space-y-6"
                >
                  <div className="border-b border-[#2A3A5F] pb-4 mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="eyebrow">Required Documents <span className="text-[#FF3B30]">*</span></div>
                      <div className="text-[10px] text-[#FF3B30] font-semibold tracking-wider">MANDATORY</div>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-3">
                      Per AMSC India 2026 rules, {form.nationality === "India" ? "SFI UID document" : "passport copy"} and medical fitness certificate are mandatory. PDF or image · max 5MB each. <strong className="text-[#FBBF24]">Document verification by admin takes 1–2 working days.</strong> You can browse but cannot register for events until documents are verified.
                    </p>
                    <div className="p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg">
                      <h4 className="text-[#00F0FF] text-sm font-semibold mb-1">Medical Certificate Instructions</h4>
                      <p className="text-xs text-[#94A3B8] mb-2">
                        <strong>Click the Medical Fitness Certificate box below</strong> to either download a draft template or upload your signed copy.
                      </p>
                      <ul className="text-xs text-[#94A3B8] list-disc list-inside space-y-1">
                        <li>Download and print the draft.</li>
                        <li>Get it filled and signed by a registered medical practitioner.</li>
                        <li>Scan or take a clear photo of the signed document.</li>
                        <li>Click the box below again to upload it.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-4">
                      <DocUpload
                        label={form.nationality === "India" ? "SFI UID Document *" : "Passport Copy *"}
                        icon={FileText}
                        testid="reg-passport-doc"
                        attached={!!form.passport_document}
                        onChange={onDoc("passport_document", "passport")}
                      />
                      <Field label={`${form.nationality === "India" ? "SFI UID" : "Passport"} Expiry (MM-YY)`} required>
                        <input
                          data-testid="reg-passport-expiry"
                          type="text"
                          required
                          placeholder="MM-YY"
                          pattern="\d{2}-\d{2}"
                          maxLength={5}
                          value={form.passport_expiry_date}
                          onChange={(e) => {
                            let val = e.target.value.replace(/\D/g, "");
                            if (val.length > 2) {
                              val = val.substring(0, 2) + "-" + val.substring(2, 4);
                            }
                            update("passport_expiry_date", val);
                          }}
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val && val.length === 5) {
                              const [mm, yy] = val.split("-");
                              const month = parseInt(mm, 10);
                              const year = parseInt(yy, 10) + 2000;
                              if (month < 1 || month > 12) {
                                toast.error("Invalid month — please enter a valid MM-YY format.");
                                update("passport_expiry_date", "");
                              } else if (year < 2027 || (year === 2027 && month < 3)) {
                                toast.error(`${form.nationality === "India" ? "SFI UID" : "Passport"} must be valid until at least March 2027.`);
                                update("passport_expiry_date", "");
                              }
                            }
                          }}
                          className="input-dark"
                        />
                      </Field>
                    </div>
                    <div>
                      <MedicalDocUpload
                        attached={!!form.medical_certificate}
                        onChange={onDoc("medical_certificate", "medical")}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="divider-line my-6" />

          {/* Navigation Controls */}
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="btn-secondary flex-1 justify-center min-h-[44px] cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {step < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="btn-primary flex-1 justify-center min-h-[44px] cursor-pointer"
                data-testid="next-step-btn"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid="reg-submit"
                type="submit"
                disabled={loading}
                className="btn-primary flex-1 justify-center min-h-[44px] cursor-pointer"
              >
                {loading ? "Creating account..." : "Create Account"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="text-sm text-[#94A3B8] text-center pt-2">
            Already registered? <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="link-to-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
      {label} {required && <span className="text-[#00F0FF]">*</span>}
    </label>
    {children}
  </div>
);

const DocUpload = ({ label, icon: Icon, testid, attached, onChange }) => (
  <label
    className={`block rounded-lg border border-dashed p-4 cursor-pointer transition group ${attached ? "border-[#00E676] bg-[#00E676]/5" : "border-[#2A3A5F] bg-[#0B1429] hover:border-[#00F0FF]/50"
      }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded flex items-center justify-center shrink-0 ${attached ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#142040] text-[#00F0FF] group-hover:bg-[#00F0FF]/10"}`}>
        {attached ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs text-[#94A3B8] truncate">
          {attached ? "Attached — click to replace" : "PDF or image · max 5MB"}
        </div>
      </div>
    </div>
    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onChange} />
  </label>
);

const MedicalDocUpload = ({ attached, onChange }) => {
  const [open, setOpen] = React.useState(false);

  if (open) {
    return (
      <div className="block rounded-lg border border-[#00F0FF]/50 p-4 bg-[#0B1429]">
        <div className="text-sm font-semibold mb-3 text-center">Medical Certificate Options</div>
        <div className="space-y-3">
          <a href="/medical-certificate-draft.pdf" target="_blank" rel="noopener noreferrer" className="btn-secondary w-full justify-center text-xs flex items-center gap-2">
            <FileText className="w-4 h-4" /> Download Draft Template
          </a>
          <label className="btn-primary w-full justify-center text-xs cursor-pointer flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Upload Signed Certificate
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => {
              onChange(e);
              setOpen(false);
            }} />
          </label>
          <button type="button" onClick={() => setOpen(false)} className="text-xs text-[#94A3B8] w-full text-center hover:text-white pt-1 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`block rounded-lg border border-dashed p-4 cursor-pointer transition group ${attached ? "border-[#00E676] bg-[#00E676]/5" : "border-[#2A3A5F] bg-[#0B1429] hover:border-[#00F0FF]/50"}`}
      onClick={() => setOpen(true)}
      data-testid="reg-medical-doc"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded flex items-center justify-center shrink-0 ${attached ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#142040] text-[#00F0FF] group-hover:bg-[#00F0FF]/10"}`}>
          {attached ? <Check className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Medical Fitness Certificate *</div>
          <div className="text-xs text-[#94A3B8] truncate">
            {attached ? "Attached — click for options" : "Click to view options"}
          </div>
        </div>
      </div>
    </div>
  );
};

